import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { concat, trimStart } from 'lodash';
import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react';
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Alert, Button, FormGroup, Input, Label } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { CenteredContainer, Loading } from '.';
import { RegistrationTokenSummary } from '../types/graph';
import { useCurrentUser, useId } from '../hooks';
import PasswordFormGroup from './PasswordFormGroup';

type RegistrationTokenQueryResponse = {
    registrationToken: RegistrationTokenSummary
}
type registrationTokenQueryVariables = {
    token: string
}

const registrationTokenQuery = gql<RegistrationTokenQueryResponse, registrationTokenQueryVariables>`
    query ($token: String!){
        registrationToken (token: $token) {
            email,
            forceEmail,
            membership {
                user {
                    id
                }
                group {
                    id
                    name
                }
                roles {
                    id
                    name
                }
            }
        }
    }
`;

type RedeemTokenMutationResponse = {
    redeemInvitationToken: {
        isOk: boolean
    }
}
type RedeemTokenMutationVariables = {
    token: string
}

const redeemTokenMutation = gql<RedeemTokenMutationResponse, RedeemTokenMutationVariables>`
    mutation ($token: String!) {
        redeemInvitationToken(token: $token)  {
            isOk
        }
    }
`;

type RegisterMutationResponse = {
    register: {
        isOk: boolean
    }
}
type RegisterMutationVariables = {
    token: string,
    email: string,
    password: string,
    displayName: string | null
}

const registerMutation = gql<RegisterMutationResponse, RegisterMutationVariables>`
    mutation ($token: String!, $email: String!, $password: String!, $displayName: String) {
        register(token: $token, email: $email, password: $password, displayName: $displayName)  {
            isOk
        }
    }
`;

type RegistrationFormProps = {
    token: string,
    email: string | null
}



const RegistrationForm = ({ token, email: forcedEmail }: RegistrationFormProps) => {
    const { t } = useTranslation();
    const { id } = useId(6);

    const form = useForm<FieldValues>();

    const { register: registerInput, handleSubmit, formState } = form;

    const { isSubmitted, errors } = formState;

    const [registerResult, register] = useMutation(registerMutation);
    const { data: registerData, fetching: registerFetching } = registerResult;

    const [displayName, setDisplayName] = useState<string>('');

    const onSubmit = useCallback(({ email, password, displayName }: FieldValues) => {
        register({
            token,
            email,
            password,
            displayName: (displayName?.length ?? 0) === 0 ? null : displayName
        });
    }, [token, register]);

    const fieldClassNames = useCallback((field:string, cn:string[] = []) => classNames(concat(cn, isSubmitted
        ? errors[field] !== undefined
            ? 'is-invalid'
            : 'is-valid'
        : null)), [isSubmitted, errors])

    const message = useMemo(() => registerData?.register.isOk === false
        ? <Alert color="error">Problema durante la registrazione.</Alert>
        : null, [registerData]);

    if (registerData?.register.isOk === true) {
        return <>
            <Alert color="success">Registrazione effettuata, inserisci le credenziali per accedere.</Alert>

            <Button tag={Link} to="/" block color="link" type="button">
                {t('Effettua l\'accesso')}
            </Button>
        </>
    }

    return <form id={`registrationForm-${id}`} onSubmit={handleSubmit(onSubmit)} noValidate>
        {message}
        <FormGroup>
            <Label htmlFor={`email${id}`}>Email</Label>
            <input readOnly={forcedEmail !== null} id={`email${id}`} className={fieldClassNames('email', ['form-control'])} type="text" autoComplete="username" {...registerInput('email', {
                required: t('Campo obbligatorio') as string,
                value: forcedEmail,
                pattern: {
                    value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
                    message: t('Deve essere un indirizzo email valido')
                }
            })} />
            <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => <div className="invalid-feedback">{message}</div>}
            />
        </FormGroup>

        <PasswordFormGroup id={id} form={form} />

        <FormGroup>
            <Label htmlFor={`displayName${id}`}>{t('Nome visualizzato')}</Label>
            <Input id={`displayName${id}`} type="text" name="displayName" value={displayName} onChange={(e) => setDisplayName(trimStart(e.target.value))} />
        </FormGroup>

        <Button disabled={registerFetching} type="submit">{t('Invia')}</Button>
    </form>

}

const RegisterEndpoint = () => {
    const { pathname, search } = useLocation();
    const token = useMemo(() => (new URLSearchParams(search)).get('token'), [search])
    const { currentUser, fetchingCurrentUser } = useCurrentUser();
    const { t } = useTranslation('common');

    const [redirect, setRedirect] = useState<[string, object] | null>(null)

    const [registrationTokenResult] = useQuery({
        query: registrationTokenQuery,
        variables: { token: token as string },
        pause: token === null,
        requestPolicy: 'network-only'
    });

    const { data: registrationTokenData, fetching: registrationTokenFetching } = registrationTokenResult;


    const [redeemTokenResult, redeemToken] = useMutation(redeemTokenMutation);

    const { data: redeemTokenData, fetching: redeemTokenFetching } = redeemTokenResult;

    const from = `${pathname}${search}`;


    //const [signInResult, signIn] = useMutation<{ signIn: SignInResult }, {credentials: SignInCredentials }>(signInMutation);

    //const {data, fetching, error} = signInResult;

    const invite = registrationTokenData?.registrationToken;

    const handleAccess = useCallback(() => setRedirect([`/${t('access.slug')}`, { from }]), [from, setRedirect, t]);

    const handleJoin = useCallback<MouseEventHandler<HTMLButtonElement>>((e) => {
        e.preventDefault();
        if (token === null) {
            return;
        }
        redeemToken({ token });
    }, [redeemToken, token]);

    if (redirect !== null) {
        const [pathname, state] = redirect;

        return <Redirect to={{ pathname, state }} />;
    }

    if (registrationTokenFetching || redeemTokenFetching || fetchingCurrentUser) {
        return <Loading />;
    }

    if (redeemTokenData?.redeemInvitationToken?.isOk ||
        (currentUser?.id !== undefined && currentUser.id === invite?.membership?.user?.id)
    ) {
        return <CenteredContainer>Ti sei unito al gruppo {invite?.membership?.group?.name}</CenteredContainer>
    }

    if (token === null) {
        return <Redirect to="/" />;
    }

    if (invite === null || invite === undefined) {
        return <CenteredContainer>Token non valido</CenteredContainer>
    }

    const { email, forceEmail, membership } = invite;

    const noRegistration = forceEmail === true && membership?.user !== null;
    const noLogin = forceEmail === true && membership?.user === null;

    return <CenteredContainer>
        <div className="d-flex align-items-center justify-content-between flex-column">
            {
                currentUser === null
                    ? <>
                        <p>{noRegistration
                            ? t(`Registrati per unirti al gruppo ${membership?.group?.name}`)
                            : noLogin
                                ? t(`Accedi per unirti al gruppo ${membership?.group?.name}`)
                                : t(`Accedi o registrati per unirti al gruppo ${membership?.group?.name}`)}</p>

                        {noLogin ? null : <p><Button onClick={handleAccess} className='mr-2' color='primary'>Accedi</Button></p>}

                        {noLogin || noRegistration ? null : <hr />}

                        {noRegistration ? null : <div>
                            <h5>{t('Registrazione')}</h5>
                            {/*<Button tag={Link} onClick={handleRegister} to={{ pathname: `/${t('register.slug')}`, search: new URLSearchParams({ token }).toString() }} color='secondary'>Registrati</Button>*/}
                            <RegistrationForm email={invite?.forceEmail === true ? invite?.email??null : null} token={token} />
                        </div>}
                    </>
                    : invite?.forceEmail !== true || currentUser.email === email
                        ? <><p>Unisciti al gruppo {invite?.membership?.group?.name}</p>
                            <Button color='primary' onClick={handleJoin}>Unisciti</Button></>
                        : <Alert color='warning'>{t('Questo token può essere utilizzato dalla tuo utente, per poterlo utilizzare effettuare l\'uscita.')}</Alert>
            }
        </div>
    </CenteredContainer>;

    //return (
    //    redirect
    //        ? <Redirect to={{ pathname: `/${t('access.slug')}`, state: { from } }} />
    //        : <Route {...rest}>{children}</Route>
    //);
}

export default RegisterEndpoint;
